import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BRAND } from '@rydres/shared';
import { Badge, Spinner, fmtDateTime, formatUsd } from '@rydres/ui';
import { riderRides, type RiderRide } from '../lib/passenger.js';
import type { PassengerMe } from '../lib/types.js';
import { Shell, useRiderGate } from './Passenger.js';
import { STATUS_TONE } from './StatusPage.js';

const CHIP = 'inline-flex min-h-11 items-center rounded-xl px-4 text-sm font-bold';
const SECONDARY = `${CHIP} bg-card2`;
const PRIMARY = `${CHIP} bg-brand text-on-brand brand-press`;
/** Rides that can still happen; anything else is history. */
const OPEN = new Set(['requested', 'quoted', 'confirmed']);
const HISTORY_PAGE = 10;
/** A ride's own page on this site: the price to accept while one waits, else details, cancelling and receipt. */
const ridePath = (r: RiderRide) => new URL(r.quoteUrl ?? r.statusUrl).pathname;

function Avatar({ name, url }: { name: string; url: string | null }) {
  return url ? (
    <img src={url} alt="" className="h-11 w-11 shrink-0 rounded-xl object-cover" />
  ) : (
    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand font-black text-on-brand">{name.slice(0, 1)}</div>
  );
}

function RideRow({ ride: r }: { ride: RiderRide }) {
  return (
    <Link to={ridePath(r)} className="block space-y-1 rounded-2xl border border-line bg-card p-4">
      <div className="flex items-center justify-between gap-2">
        <span className="font-bold">{fmtDateTime(r.startsAt, r.driver.timezone)}</span>
        <Badge value={r.status} tone={STATUS_TONE[r.status] ?? 'muted'} />
      </div>
      <div className="truncate text-sm text-ink2">{r.pickupAddress} → {r.dropoffAddress}</div>
      <div className="text-xs text-muted">{r.driver.name ?? 'Your driver'}{r.priceCents !== null ? ` · ${formatUsd(r.priceCents)}` : ''}</div>
    </Link>
  );
}

function NextRide({ ride: r, driver }: { ride: RiderRide; driver: PassengerMe['drivers'][number] | null }) {
  const name = r.driver.name ?? 'Your driver';
  return (
    <section aria-labelledby="next-ride" className="space-y-3 rounded-3xl border border-line bg-card p-4 shadow-card">
      <div className="flex items-center justify-between gap-2">
        <h2 id="next-ride" className="text-xs font-bold uppercase tracking-wide text-muted">Next ride</h2>
        <Badge value={r.status} tone={STATUS_TONE[r.status] ?? 'muted'} />
      </div>
      <p className="text-xl font-black">{fmtDateTime(r.startsAt, r.driver.timezone)}</p>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
        <dt className="text-muted">From</dt>
        <dd className="min-w-0 break-words">{r.pickupAddress}</dd>
        <dt className="text-muted">To</dt>
        <dd className="min-w-0 break-words">{r.dropoffAddress}</dd>
      </dl>
      <div className="flex items-center gap-3">
        <Avatar name={name} url={r.driver.photoUrl} />
        <div className="min-w-0 font-bold">{name}</div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Link to={ridePath(r)} className={PRIMARY}>{r.quoteUrl ? 'See the price' : 'Details'}</Link>
        {driver?.canMessage && (
          <Link to={`/me/people?with=${encodeURIComponent(driver.handle)}`} className={SECONDARY} aria-label={`Message ${name}`}>Message</Link>
        )}
        {driver?.phone && (
          <a href={`tel:${driver.phone}`} className={SECONDARY} aria-label={`Call ${name}`}>Call</a>
        )}
      </div>
    </section>
  );
}

/** rydres.com/me: the next ride, booking again, upcoming rides and the history. */
export function HomePage() {
  const { p, gate } = useRiderGate(
    'Your rides',
    `Sign in with the mobile number you book with to see your rides, book your ${BRAND.name} drivers again and message them.`,
  );
  const [rides, setRides] = useState<RiderRide[] | null>(null);
  const [shown, setShown] = useState(HISTORY_PAGE);
  const signedIn = Boolean(p.me);
  useEffect(() => {
    if (!signedIn) return;
    const load = () => riderRides().then((r) => setRides(r.rides), () => setRides((x) => x ?? []));
    void load();
    const timer = setInterval(() => document.visibilityState === 'visible' && void load(), 60_000);
    return () => clearInterval(timer);
  }, [signedIn]);
  if (gate) return gate;

  const me = p.me!;
  const now = Date.now();
  // Still to come: open rides, including one under way (up to six hours after pickup).
  const upcoming = (rides ?? []).filter((r) => OPEN.has(r.status) && Date.parse(r.startsAt) > now - 6 * 3_600_000).sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt));
  const history = (rides ?? []).filter((r) => !upcoming.includes(r));
  const [next, ...later] = upcoming;
  const first = me.details.name?.trim().split(/\s+/)[0];

  return (
    <Shell>
      <h1 className="text-2xl font-black">{first ? `Hi, ${first}` : 'Your rides'}</h1>
      {rides === null ? (
        <Spinner />
      ) : next ? (
        <NextRide ride={next} driver={me.drivers.find((d) => d.handle === next.driver.handle) ?? null} />
      ) : (
        <p className="rounded-2xl border border-dashed border-line p-4 text-sm text-ink2">No rides coming up.</p>
      )}

      <section className="space-y-2" aria-labelledby="book">
        <h2 id="book" className="px-1 text-xs font-bold uppercase tracking-wide text-muted">Book a ride</h2>
        {me.drivers.length === 0 ? (
          <p className="px-1 text-sm text-ink2">Book any {BRAND.name} driver on their page; they show up here afterwards.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {me.drivers.map((d) => (
              <Link key={d.handle} to={`/${d.handle}`} className={SECONDARY}>
                With {d.displayName ?? d.handle}
              </Link>
            ))}
            <Link to="/me/services" className={`${CHIP} text-accent-text underline`}>Services and prices</Link>
          </div>
        )}
      </section>

      {later.length > 0 && (
        <section className="space-y-2" aria-labelledby="upcoming">
          <h2 id="upcoming" className="px-1 text-xs font-bold uppercase tracking-wide text-muted">Upcoming</h2>
          {later.map((r) => <RideRow key={r.id} ride={r} />)}
        </section>
      )}

      {history.length > 0 && (
        <section className="space-y-2" aria-labelledby="history">
          <h2 id="history" className="px-1 text-xs font-bold uppercase tracking-wide text-muted">Ride history</h2>
          {history.slice(0, shown).map((r) => <RideRow key={r.id} ride={r} />)}
          {history.length > shown && (
            <button type="button" onClick={() => setShown((n) => n + HISTORY_PAGE)} className="w-full rounded-xl bg-card2 py-3 text-sm font-bold">
              Show more
            </button>
          )}
        </section>
      )}
    </Shell>
  );
}
